/**
 * Farm & Barn Shopify Quote Integration
 * 
 * This script integrates the shipping quote system with Shopify product pages.
 * It detects products tagged with "requires-quote" and replaces the "Add to Cart"
 * button with a "Request Shipping Quote" button that opens a modal.
 * 
 * Features:
 * - Detects Shopify customer login state
 * - Fetches customer data for logged-in users
 * - Opens quote form in modal popup
 * - Passes product data (ID, title, price, weight, image)
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    QUOTE_APP_URL: 'https://shipquote-hdwabfsa.manus.space',
    REQUIRED_TAG: 'requires-quote',
    BUTTON_TEXT: 'Request Shipping Quote',
    DEBUG: false,
  };

  function log(...args) {
    if (CONFIG.DEBUG) {
      console.log('[Quote Integration]', ...args);
    }
  }

  /**
   * Check if customer is logged into Shopify
   */
  function getShopifyCustomerId() {
    try {
      // Method 1: Check ShopifyAnalytics
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) {
        const customerId = window.ShopifyAnalytics.meta.page.customerId;
        if (customerId) {
          log('Customer ID from ShopifyAnalytics:', customerId);
          return customerId;
        }
      }

      // Method 2: Check meta tags
      const customerMeta = document.querySelector('meta[name="shopify-customer-id"]');
      if (customerMeta) {
        const customerId = customerMeta.getAttribute('content');
        log('Customer ID from meta tag:', customerId);
        return customerId;
      }

      // Method 3: Check window.meta
      if (window.meta && window.meta.page && window.meta.page.customerId) {
        const customerId = window.meta.page.customerId;
        log('Customer ID from window.meta:', customerId);
        return customerId;
      }

      log('No customer logged in');
      return null;
    } catch (error) {
      log('Error getting customer ID:', error);
      return null;
    }
  }

  /**
   * Get product data from page
   */
  function getProductData() {
    try {
      // Try to find product JSON
      const productJsonElement = document.querySelector('[data-product-json]');
      if (productJsonElement) {
        const productData = JSON.parse(productJsonElement.textContent);
        log('Product data:', productData);
        return productData;
      }

      // Fallback: try window.ShopifyAnalytics
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
        const productData = window.ShopifyAnalytics.meta.product;
        log('Product data from analytics:', productData);
        return productData;
      }

      log('No product data found');
      return null;
    } catch (error) {
      log('Error getting product data:', error);
      return null;
    }
  }

  /**
   * Check if product has required tag
   */
  function hasRequiredTag(product) {
    if (!product || !product.tags) return false;

    const tags = Array.isArray(product.tags) ? product.tags : product.tags.split(',').map(t => t.trim());
    const normalizedTags = tags.map(tag => String(tag).toLowerCase().trim());
    const requiredTag = CONFIG.REQUIRED_TAG.toLowerCase();

    log('Product tags:', tags);
    log('Looking for tag:', requiredTag);
    log('Normalized tags:', normalizedTags);

    return normalizedTags.includes(requiredTag);
  }

  /**
   * Get current variant or first variant
   */
  function getCurrentVariant(product) {
    if (!product || !product.variants || product.variants.length === 0) {
      return null;
    }

    // Try to get selected variant from form
    const variantIdInput = document.querySelector('select[name="id"], input[name="id"]');
    if (variantIdInput) {
      const variantId = parseInt(variantIdInput.value);
      const variant = product.variants.find(v => v.id === variantId);
      if (variant) {
        log('Selected variant:', variant);
        return variant;
      }
    }

    // Fallback to first variant
    log('Using first variant:', product.variants[0]);
    return product.variants[0];
  }

  /**
   * Build quote URL with product and customer data
   */
  function buildQuoteUrl(product, variant, customerId) {
    // Get product image
    let imageUrl = '';
    if (product.featured_image) {
      imageUrl = product.featured_image;
    } else if (product.images && product.images.length > 0) {
      imageUrl = product.images[0];
    } else if (variant.featured_image) {
      imageUrl = variant.featured_image.src;
    }

    const params = new URLSearchParams({
      productId: String(product.id),
      variantId: String(variant.id),
      productTitle: product.title,
      variantTitle: variant.title || '',
      sku: variant.sku || '',
      price: String(variant.price / 100), // Convert cents to dollars
      weight: String(variant.weight || ''),
      weightUnit: variant.weight_unit || 'kg',
      imageUrl: imageUrl,
    });

    if (customerId) {
      params.append('shopifyCustomerId', String(customerId));
    }

    return `${CONFIG.QUOTE_APP_URL}/quote/request?${params.toString()}`;
  }

  /**
   * Create and show modal popup
   */
  function showModal(url) {
    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.id = 'quote-modal-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 999999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
    `;

    // Create modal container
    const modal = document.createElement('div');
    modal.id = 'quote-modal-container';
    modal.style.cssText = `
      position: relative;
      width: 100%;
      max-width: 1200px;
      height: 90vh;
      max-height: 900px;
      background: white;
      border-radius: 20px;
      overflow: hidden;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    `;

    // Create iframe
    const iframe = document.createElement('iframe');
    iframe.src = url;
    iframe.style.cssText = `
      width: 100%;
      height: 100%;
      border: none;
    `;

    modal.appendChild(iframe);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Close on overlay click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closeModal();
      }
    });

    // Listen for messages from iframe
    window.addEventListener('message', handleModalMessage);

    log('Modal opened');
  }

  /**
   * Handle messages from modal iframe
   */
  function handleModalMessage(event) {
    // Security: check origin if needed
    // if (event.origin !== CONFIG.QUOTE_APP_URL) return;

    const data = event.data;
    log('Received message:', data);

    if (data.type === 'close-modal') {
      closeModal();
    } else if (data.type === 'quote-submitted') {
      closeModal();
      // Show success message
      alert('Quote request submitted successfully! Check your email for updates.');
    }
  }

  /**
   * Close modal
   */
  function closeModal() {
    const overlay = document.getElementById('quote-modal-overlay');
    if (overlay) {
      overlay.remove();
    }

    // Restore body scroll
    document.body.style.overflow = '';

    // Remove message listener
    window.removeEventListener('message', handleModalMessage);

    log('Modal closed');
  }

  /**
   * Replace Add to Cart button with Quote button
   */
  function replaceAddToCartButton() {
    const product = getProductData();
    if (!product) {
      log('No product data available');
      return;
    }

    if (!hasRequiredTag(product)) {
      log('Product does not have required tag');
      return;
    }

    log('✓ Product requires quote - replacing button');

    const variant = getCurrentVariant(product);
    if (!variant) {
      log('No variant found');
      return;
    }

    const customerId = getShopifyCustomerId();
    const quoteUrl = buildQuoteUrl(product, variant, customerId);

    // Find and replace Add to Cart buttons
    const selectors = [
      'button[name="add"]',
      'button[type="submit"].product-form__submit',
      '.product-form__submit',
      '[data-add-to-cart]',
      '.add-to-cart',
      'button.btn--add-to-cart',
    ];

    let replaced = false;
    for (const selector of selectors) {
      const buttons = document.querySelectorAll(selector);
      buttons.forEach(button => {
        // Create quote button
        const quoteButton = button.cloneNode(true);
        quoteButton.textContent = CONFIG.BUTTON_TEXT;
        quoteButton.type = 'button';
        quoteButton.name = '';
        
        // Remove form submission
        quoteButton.removeAttribute('name');
        quoteButton.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          showModal(quoteUrl);
          return false;
        };

        // Replace button
        button.parentNode.replaceChild(quoteButton, button);
        replaced = true;
        log('✓ Replaced button:', selector);
      });
    }

    if (!replaced) {
      log('⚠ No Add to Cart buttons found to replace');
    }
  }

  /**
   * Initialize when DOM is ready
   */
  function init() {
    log('=== Initializing Quote Integration ===');
    log('Config:', CONFIG);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', replaceAddToCartButton);
    } else {
      replaceAddToCartButton();
    }

    // Watch for variant changes
    document.addEventListener('change', (e) => {
      if (e.target.name === 'id' || e.target.matches('[name="id"]')) {
        log('Variant changed, updating button');
        setTimeout(replaceAddToCartButton, 100);
      }
    });
  }

  // Start
  init();
})();
